import {ROUTE_RULES} from '../constants/routes';
import {BackendTestAppsEnvironment} from '../types/auth';
import {RouteFamily, type RouteRule} from '../types/route';
import {resolveDefaultOrigin} from '../utils/url';

const readBackendTestEnvironment = ():
  | BackendTestAppsEnvironment
  | undefined => {
  const environment = process.env.NEXT_PUBLIC_BE_TEST_APPS_ENVIRONMENT;

  if (
    environment === BackendTestAppsEnvironment.ALPHA ||
    environment === BackendTestAppsEnvironment.BETA ||
    environment === BackendTestAppsEnvironment.GAMMA
  ) {
    return environment;
  }

  return undefined;
};

/** Returns the Plutus API gateway host, appending the test environment suffix (alpha/beta/gamma) when configured. */
export const resolvePlutusGatewayHost = (): string | undefined => {
  const host = process.env.NEXT_PUBLIC_API_GATEWAY_PLUTUS;

  if (!host) {
    return undefined;
  }

  const environment = readBackendTestEnvironment();

  if (!environment) {
    return host;
  }

  return `${host.replace(/\/$/, '')}/${environment}`;
};

export const resolveCoreGatewayHost = (): string | undefined => {
  return process.env.NEXT_PUBLIC_API_GATEWAY_CORE;
};

/** Apollo agents-gateway host. Unlike core/plutus, this is server-only
 *  (no `NEXT_PUBLIC_` prefix) — the agents gateway is reached only from
 *  Next.js route handlers / server components, never directly from the
 *  browser. */
export const resolveApolloGatewayHost = (): string | undefined => {
  return process.env.APOLLO_GATEWAY_URL;
};

export const resolveCoreWebHost = (): string | undefined => {
  return process.env.NEXT_PUBLIC_CORE_HOST;
};

export const resolveGatewayTarget = (
  family: RouteFamily,
): string | undefined => {
  if (family === RouteFamily.LOCAL) {
    return resolveDefaultOrigin();
  }

  if (family === RouteFamily.PLUTUS || family === RouteFamily.PLUTUS_CC) {
    return resolvePlutusGatewayHost();
  }

  if (family === RouteFamily.CORE || family === RouteFamily.CORE_CC) {
    return resolveCoreGatewayHost();
  }

  if (family === RouteFamily.APOLLO_CC) {
    return resolveApolloGatewayHost();
  }

  return resolveCoreWebHost();
};

let cachedRouteRules: RouteRule[] | null = null;

export const buildRouteRules = (): RouteRule[] => {
  if (cachedRouteRules) {
    return cachedRouteRules;
  }

  cachedRouteRules = ROUTE_RULES.map((rule) =>
    Object.assign({}, rule, {target: resolveGatewayTarget(rule.family)}),
  );

  return cachedRouteRules;
};

/** Clears the cached route rules so they are rebuilt on next access. For use in tests when env vars change between runs. */
export const resetRouteRulesCache = (): void => {
  cachedRouteRules = null;
};

export const findMatchingRouteRule = (
  pathname: string,
): RouteRule | undefined => {
  return buildRouteRules().find((rule) => pathname.startsWith(rule.prefix));
};

export const resolveBackendTestEnvironment = readBackendTestEnvironment;
